/**
 * Adapter that bridges react-router's `useParams` to the framework-free
 * `<WorkspaceProvider>` shipped from `@cometchat-ai/auth/client`. The
 * library deliberately doesn't import react-router (so it can ship to any
 * React app); this thin wrapper pulls `:slug` from the URL and feeds it
 * in, plus mounts the `<WorkspaceShell>` chrome.
 */

import { useAuth, WorkspaceProvider, type Permission } from "@cometchat-ai/auth/client";
import { Spinner } from "@cometchat-ai/ui-dashboard";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate, useParams } from "react-router";

import { apiRequest } from "../agents/api/client";
import { WorkspaceShell } from "../layouts/workspace-shell";
import { onboardingKeys, type OnboardingOverview } from "../onboarding/api/onboarding";
import { camelizeKeys } from "../tables/api/camelize";

/**
 * Platform-staff bypass (design WP-6, B1/B2 slice 4): an FDE / internal
 * operator always gets the full dashboard even on an onboarding-mode
 * workspace. This is now governed by the `platform:staff` role (resolved into
 * the principal's permissions, B1) — replacing the prior `@cometchat.com`
 * email-suffix heuristic — so support access is role-based and auditable.
 * Routing-only; mirrors `RootRedirect`.
 */
function isPlatformStaff(permissions: ReadonlyArray<Permission>): boolean {
  return permissions.includes("platform:staff");
}

export function WorkspaceRoute() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const auth = useAuth();
  const { t } = useTranslation("auth");

  // Surface gate (design WP-6): onboarding-mode workspaces are confined to
  // `/onboard`. `RootRedirect` enforces this at `/`, but post-login navigation
  // lands directly on `/w/:slug` (see `login.tsx` / `auth-handoff`), so without
  // this the gate is bypassed and an onboarding workspace shows the full
  // dashboard. Shares the cached overview query with `RootRedirect`.
  const { data: overview, isPending: overviewPending } = useQuery({
    queryKey: onboardingKeys.overview(slug ?? ""),
    queryFn: () =>
      apiRequest<unknown>("GET", "/api/onboarding/overview").then(camelizeKeys<OnboardingOverview>),
    enabled: Boolean(slug && auth.status === "authenticated"),
    staleTime: 30_000,
  });

  if (!slug) {
    return <Navigate to="/" replace />;
  }

  // Wait for the surface flag before rendering chrome (avoids a dashboard
  // flash before redirecting an onboarding-mode workspace to `/onboard`).
  if (overviewPending) {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <Spinner aria-label={t("bootstrap.loading")} />
      </div>
    );
  }

  if (overview?.surfaceMode === "onboarding" && !isPlatformStaff(auth.permissions)) {
    return <Navigate to="/onboard" replace />;
  }

  return (
    <WorkspaceProvider
      slug={slug}
      onNotFound={() => navigate("/no-workspace", { replace: true })}
      fallback={
        <div className="flex min-h-dvh items-center justify-center">
          <Spinner aria-label={t("bootstrap.loading")} />
        </div>
      }
    >
      <WorkspaceShell />
    </WorkspaceProvider>
  );
}
